import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Download, Plus } from "lucide-react";
import { api } from "../utils/api";
import { downloadCsv } from "../utils/csv";
import { fmtDate } from "../utils/fmt";
import { useAuth } from "../contexts/AuthContext";
import AttachmentUpload from "../components/AttachmentUpload";
import FilterBar, { matchFilters } from "../components/FilterBar";
import StatusSegments from "../components/StatusSegments";

const CATEGORIES = ["SAP", "Other Applications", "Hardware Support", "Software Support", "Network & Infrastructure", "IT Security", "Access Management"];
const PRIORITIES = ["low", "medium", "high", "critical"];
const PLANTS = ["TGS", "KASESE", "NAMANVE"];
const STATUSES = ["logged", "assigned", "in_progress", "pending_user_info", "pending_vendor_info", "resolved", "closed", "reopened", "canceled"];

const PRIORITY_BADGE = {
  low: "bg-gray-100 text-gray-700",
  medium: "bg-blue-100 text-blue-700",
  high: "bg-orange-100 text-orange-700",
  critical: "bg-red-100 text-red-700",
};

export default function Incidents() {
  const { user, hasRole } = useAuth();
  const isIT = hasRole("admin", "it_team", "it_manager", "it_security");
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [segment, setSegment] = useState("open"); // v2.0 #9
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: CATEGORIES[0], priority: "medium", plant: "TGS", attachments: [], reporter_id_override: "" });
  const [suggestions, setSuggestions] = useState([]);

  async function load() {
    const { data } = await api.get("/api/incidents");
    setItems(data);
  }
  useEffect(() => {
    load();
    api.get("/api/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  // KB suggestions for the title field
  useEffect(() => {
    if (!show || form.title.length < 3) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/api/kb/suggest?title=${encodeURIComponent(form.title)}&limit=3`);
        setSuggestions(data || []);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [form.title, show]);

  async function create(e) {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.reporter_id_override) delete payload.reporter_id_override;
    await api.post("/api/incidents", payload);
    setShow(false);
    setForm({ title: "", description: "", category: CATEGORIES[0], priority: "medium", plant: "TGS", attachments: [], reporter_id_override: "" });
    load();
  }

  function exportCsv() {
    downloadCsv(`incidents-${new Date().toISOString().slice(0, 10)}.csv`, filtered, [
      "incident_number", "title", "category", "priority", "plant", "status",
      "reporter_name", "assigned_to_name", "is_overdue", "deadline", "created_at", "closed_at",
    ]);
  }

  const filtered = items.filter((it) => {
    // v2.0 #9: status segments
    if (segment === "open" && ["resolved", "closed", "canceled"].includes(it.status)) return false;
    if (segment === "resolved" && it.status !== "resolved") return false;
    if (segment === "closed" && it.status !== "closed") return false;
    if (segment === "canceled" && it.status !== "canceled") return false;
    if (!matchFilters(it, filters)) return false;
    if (search) {
      const hay = `${it.incident_number} ${it.title} ${it.reporter_name || ""} ${it.assigned_to_name || ""}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  const filterOptions = [
    { key: "status", label: "Status", values: STATUSES },
    { key: "priority", label: "Priority", values: PRIORITIES },
    { key: "plant", label: "Plant", values: PLANTS },
    { key: "category", label: "Category", values: CATEGORIES },
    { key: "is_overdue", label: "Overdue", values: [{ value: "yes", label: "Overdue only" }, { value: "no", label: "Not overdue" }] },
    { key: "assignee", label: "Assigned to", values: users.filter((u) => ["it_team", "it_manager", "it_security", "admin"].includes(u.role)).map((u) => ({ value: u.id, label: u.full_name })) },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Incidents <span className="text-sm font-normal text-gray-500">({filtered.length}{filtered.length !== items.length && `/${items.length}`})</span></h1>
        <div className="flex gap-2">
          <button className="btn-secondary flex items-center gap-2" onClick={exportCsv}><Download size={16} /> CSV</button>
          <button className="btn-primary flex items-center gap-2" onClick={() => setShow(true)}>
            <Plus size={16} /> New Incident
          </button>
        </div>
      </div>

      <StatusSegments
        value={segment} onChange={setSegment} items={items}
        segments={[
          { key: "open",     label: "Open",     match: (x) => !["resolved","closed","canceled"].includes(x.status) },
          { key: "resolved", label: "Resolved", match: (x) => x.status === "resolved" },
          { key: "closed",   label: "Closed",   match: (x) => x.status === "closed" },
          { key: "canceled", label: "Cancelled",match: (x) => x.status === "canceled" },
          { key: "all",      label: "All",      match: () => true },
        ]}
      />

      <FilterBar
        filters={filters} onChange={setFilters}
        options={filterOptions}
        onClear={() => setFilters({})}
        search={search} onSearchChange={setSearch}
        searchPlaceholder="Search by title, number, reporter, assignee…"
      />

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              <th className="p-3">Number</th>
              <th className="p-3">Title</th>
              <th className="p-3">Caller</th>
              <th className="p-3">Category</th>
              <th className="p-3">Priority</th>
              <th className="p-3">Plant</th>
              <th className="p-3">Status</th>
              <th className="p-3">Assigned To</th>
              <th className="p-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it) => (
              <tr key={it.id} className="border-t hover:bg-gray-50">
                <td className="p-3 font-mono text-xs">
                  <Link className="text-brand-600 hover:underline" to={`/incidents/${it.id}`}>{it.incident_number}</Link>
                </td>
                <td className="p-3">{it.title}{it.is_overdue && <span className="ml-2 badge bg-red-100 text-red-700">OVERDUE</span>}</td>
                <td className="p-3 text-gray-700">{it.reporter_name || "—"}</td>
                <td className="p-3">{it.category}</td>
                <td className="p-3"><span className={`badge badge-${it.priority}`}>{it.priority}</span></td>
                <td className="p-3">{it.plant}</td>
                <td className="p-3"><span className={`badge status-${it.status}`}>{it.status.replace(/_/g, " ")}</span></td>
                <td className="p-3">{it.assigned_to_name || "—"}</td>
                <td className="p-3 text-gray-500 text-xs">{fmtDate(it.created_at)}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="9" className="p-8 text-center text-gray-500">No incidents match the current filters</td></tr>}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <form onSubmit={create} className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="bg-brand-500 text-white px-5 py-3 rounded-t-lg font-semibold">Report an Incident</div>
            <div className="p-5 space-y-3">
              <div>
                <label className="label">Title</label>
                <input className="input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                {suggestions.length > 0 && (
                  <div className="mt-2 bg-brand-50 border border-brand-200 rounded p-2">
                    <div className="text-xs font-medium text-brand-700 flex items-center gap-1 mb-1">
                      <BookOpen size={12} /> Knowledge base suggestions — check before logging:
                    </div>
                    <ul className="space-y-1">
                      {suggestions.map((s) => (
                        <li key={s.id}>
                          <a href={`/knowledge-base/${s.id}`} target="_blank" rel="noreferrer" className="text-sm text-brand-700 hover:underline">
                            • {s.title} <span className="text-xs text-gray-500">({s.category})</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div><label className="label">Description</label><textarea className="input" rows="4" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              {isIT && (
                <div>
                  <label className="label">
                    Caller / Requester <span className="text-xs text-gray-400 font-normal">(if logging on behalf of someone else)</span>
                  </label>
                  <select className="input" value={form.reporter_id_override}
                    onChange={(e) => setForm({ ...form, reporter_id_override: e.target.value })}>
                    <option value="">— Me ({user?.full_name}) —</option>
                    {users.filter((u) => u.is_active !== false && u.id !== user?.id).map((u) =>
                      <option key={u.id} value={u.id}>{u.full_name} ({u.role}{u.plant ? ` · ${u.plant}` : ""})</option>
                    )}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Category</label><select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></div>
                <div><label className="label">Priority</label><select className="input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>{PRIORITIES.map((p) => <option key={p}>{p}</option>)}</select></div>
              </div>
              <div><label className="label">Plant</label><select className="input" value={form.plant} onChange={(e) => setForm({ ...form, plant: e.target.value })}>{PLANTS.map((p) => <option key={p}>{p}</option>)}</select></div>
              <AttachmentUpload value={form.attachments} onChange={(a) => setForm({ ...form, attachments: a })} />
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 bg-gray-50 rounded-b-lg">
              <button type="button" className="btn-secondary" onClick={() => setShow(false)}>Cancel</button>
              <button className="btn-primary">Create</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
